/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import { TabsItem } from 'types/tabsItem'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { removeStudyGuide, removeStudyGuideVariables } from '__generatedTypes__/removeStudyGuide'
import { DELETE_STUDY_GUIDE } from 'queries/studyGuide'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { BlogPostList, BlogPostListCopy } from '../BlogPostList'
import { useBlogPostListSearchParams } from '../BlogPostList/hooks'
import { StudyGuidesQueryParams, useStudyGuidesQuery } from './hooks'
import { Props, StudyGuideListItem } from './types'

type AllStudyGuidesContentProps = Props & {
  status?: TabsItem
  showStatusTabs?: boolean
}

const STUDY_GUIDE_LIST_COPY: BlogPostListCopy = {
  deleteConfirmationSubtitle: 'Are you sure? This will permanently delete the study guide from the database.',
  deleteErrorMessage: 'Failed to delete study guide',
  deleteSuccessMessage: 'Study guide deleted successfully',
  deleteTitle: 'Deleting Study Guide',
  entityName: 'study guide',
  titleColumn: 'Study Guide Title',
}

export const AllStudyGuidesContent = ({ platform, status, showStatusTabs = true }: AllStudyGuidesContentProps) => {
  const { getLink } = useLink()
  const { search, activeStatus: queryActiveStatus, page, take, updateTableParams } = useBlogPostListSearchParams()
  const activeStatus = status ?? queryActiveStatus
  const handleQueryParamsChange = useCallback(
    (params: StudyGuidesQueryParams) => {
      const nextStatusId = params.filters.statusId ?? activeStatus.id

      if (
        params.page === page &&
        params.take === take &&
        params.search === search &&
        nextStatusId === activeStatus.id
      ) {
        return
      }

      updateTableParams({
        page: params.page,
        take: params.take,
        search: params.search,
        status: nextStatusId,
      })
    },
    [activeStatus.id, page, search, take, updateTableParams]
  )

  const studyGuidesQuery = useStudyGuidesQuery({
    page,
    take,
    search,
    platformId: platform.id,
    statusId: activeStatus.id,
    onChange: handleQueryParamsChange,
  })

  const [removeStudyGuide, removeStudyGuideMutation] = useMutation<removeStudyGuide, removeStudyGuideVariables>(
    DELETE_STUDY_GUIDE
  )

  const handleDelete = async (studyGuide: StudyGuideListItem) => {
    await removeStudyGuide({
      variables: {
        removeStudyGuideId: studyGuide.id,
      },
    })

    await studyGuidesQuery.refetchCurrent()
  }

  return (
    <BlogPostList
      copy={STUDY_GUIDE_LIST_COPY}
      getEditLink={(studyGuide) => getLink(cmsRoutes.EDIT_STUDY_GUIDE, { studyGuideId: studyGuide.id })}
      getSectionLink={(section) => getLink(cmsRoutes.ORGANIZE_EDIT_STUDY_GUIDES, { id: section.id })}
      isDeleting={removeStudyGuideMutation.loading || studyGuidesQuery.loading}
      isLoading={!studyGuidesQuery.loaded}
      items={studyGuidesQuery.items}
      onDelete={handleDelete}
      showStatusTabs={showStatusTabs}
      status={status}
      totalCount={studyGuidesQuery.count}
    />
  )
}

export const AllStudyGuides = withPlatformCheck(AllStudyGuidesContent)
